import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import UserNav from "../../components/UserNav/UserNav";
import Sidebar from "../../components/Sidebar/Sidebar";
import UserModal from "../../components/EmailModal/UserModal";
import Storage from "../../utils/storage";
import {
  checkTerminalStatus,
  resetTerminal,
  bulkUnlockTerminals,
  getCertificates,
  getViolationReasons,
  clearBulkUnlockResponse,
  clearErrorResponse,
} from "../../redux/Actions/PosActions";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import TERMINAL_RESET_TERMS_URL from "../../assets/documents/nibss-geofencing-terms.pdf";
import Alert from "../../utils/Alert";

// Keep this version in sync with the backend consent validation.
const TERMINAL_RESET_TERMS_VERSION = "nibss-geofencing-terms-v1";

const showConsentAuditNotice = (response) => {
  const body = response?.data;
  const message = body?.audit_warning;
  if (message) Alert({ message }, "warning", "Audit warning", message);
  // The existing responseHandler covers ordinary errors. This 503 flag is
  // specific to the new consent check stopping a reset before it starts.
  if (body?.code === 503 && body?.data?.reset_not_started === true) {
    Alert(body, "danger", "Error Message.");
  }
};

const ResultRow = ({ label, value }) => (
  <div className="modal-input-container" style={{ marginBottom: "0.75rem" }}>
    <label className="modal-input-label">{label}</label>
    <input className="modal-input-input" value={value || "—"} readOnly />
  </div>
);

export const TerminalManagement = ({
  checkTerminalStatus,
  resetTerminal,
  bulkUnlockTerminals,
  getCertificates,
  getViolationReasons,
  clearBulkUnlockResponse,
  isCheckingStatus,
  isResetting,
  isBulkUnlocking,
  terminalStatusResponse,
  bulkUnlockResponse,
  certificates,
  error,
  clearErrorResponse,
  isGettingViolationReasons,
  violationReasons,
}) => {
  const [terminalId, setTerminalId] = useState("");
  const [comment, setComment] = useState("");
  const [otherReason, setOtherReason] = useState("");
  // const [resetReason, setResetReason] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const [bulkFile, setBulkFile] = useState(null);
  const [bulkComment, setBulkComment] = useState("");
  const [bulkOtherReason, setBulkOtherReason] = useState("");
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef(null);
  const [failedPage, setFailedPage] = useState(1);
  const [successPage, setSuccessPage] = useState(1);
  const [activeTab, setActiveTab] = useState("single");
  const [innerError, setInnerError] = useState("")

  const [hasAcceptedResetTerms, setHasAcceptedResetTerms] = useState(false);
  const [hasAcceptedBulkResetTerms, setHasAcceptedBulkResetTerms] = useState(false);
  const resetConsentInFlightRef = useRef(false);
  const bulkConsentInFlightRef = useRef(false);
  const terminalManagementRef = useRef(null);
  const [confirmModalStyles, setConfirmModalStyles] = useState(undefined);

  const FAILED_PAGE_SIZE = 10;

  useEffect(() => {
    getCertificates();
    getViolationReasons();
  }, []); // eslint-disable-line

  // console.log(error)
  // console.log(error?.data?.apiErrors?.apiErrorList?.[0]?.message)
  // // console.log(violationReasons)

  useEffect(() => {
    setFailedPage(1);
  }, [bulkUnlockResponse]);

  useEffect(() => {
    setSuccessPage(1);
  }, [bulkUnlockResponse]);

  useEffect(() => {
    setHasAcceptedResetTerms(false);
  }, [activeTab]); //terminalId, comment, otherReason, 

  useEffect(() => {
    setHasAcceptedBulkResetTerms(false);
  }, [activeTab]); // bulkFile, bulkComment, bulkOtherReason, 

  useLayoutEffect(() => {
    if (!showConfirmModal || !terminalManagementRef.current) return;
    const panel = terminalManagementRef.current;
    const updateModalPosition = () => {
      const bounds = panel.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const left = Math.max(0, bounds.left);
      const right = Math.min(viewportWidth, bounds.right);
      if (right <= left) return;
      // Rodal's auto margins center the dialog between these panel edges.
      setConfirmModalStyles({
        left,
        right: viewportWidth - right,
        maxWidth: Math.max(0, right - left - 32),
      });
    };
    updateModalPosition();
    window.addEventListener("resize", updateModalPosition);
    const observer = typeof ResizeObserver === "undefined"
      ? null : new ResizeObserver(updateModalPosition);
    if (observer) {
      observer.observe(panel);
      if (panel.parentElement) observer.observe(panel.parentElement);
    }
    return () => {
      window.removeEventListener("resize", updateModalPosition);
      if (observer) observer.disconnect();
    };
  }, [showConfirmModal]);

  const userStatus = Storage.get("user-access-data");
  const user = userStatus ? JSON.parse(userStatus) : null;
  const isStaff = user && user.type === "staff";

  const openConfirm = (action) => {
    if (!terminalId.trim()) return;
    setHasAcceptedResetTerms(false);
    setPendingAction(action);
    setShowConfirmModal(true);
    setComment("");
    setOtherReason("");
  };

  const handleConfirm = async () => {
    if (pendingAction === "reset" &&
      (!hasAcceptedResetTerms || resetConsentInFlightRef.current)) return;

    if (pendingAction === "status") {
      setShowResults(false);

      await checkTerminalStatus({
        terminal_id: terminalId,
      });

      setShowResults(true);
    }

    if (pendingAction === "reset") {
      resetConsentInFlightRef.current = true;
      const resetTerminalId = terminalId;

      // await resetTerminal({
      //   terminal_id: resetTerminalId,
      //   comment:
      //     comment === "Others"
      //       ? otherReason.trim()
      //       : comment,
      // });

      // Automatically fetch the terminal's new status
      // after the reset operation completes.
      // await checkTerminalStatus({
      //   terminal_id: resetTerminalId,
      // });

      try {
        const response = await resetTerminal({
          terminal_id: resetTerminalId,
          comment:
            comment === "Others"
              ? otherReason.trim()
              : comment,
          consent_accepted: hasAcceptedResetTerms,
          consent_terms_version: TERMINAL_RESET_TERMS_VERSION,
        });

        showConsentAuditNotice(response);

        // console.log("===== RESET RESPONSE =====");
        // console.log(response)
        // Only check status if the reset was successful
        if (response?.ok) {
          setShowResults(false);

          await checkTerminalStatus({
            terminal_id: resetTerminalId,
          });

          setShowResults(true);
        }
      } catch (error) {
        // Reset failed.
        // Do NOT call checkTerminalStatus.
        // // console.error("Terminal reset failed:", error);
      } finally {
        resetConsentInFlightRef.current = false;
        setHasAcceptedResetTerms(false);
      }

      setShowResults(true);

      setComment("");
      setOtherReason("");
    }

    setShowConfirmModal(false);
    setPendingAction(null);
  };

  const OTHER_REASON_MAX_LENGTH = 200;
  const OTHER_REASON_REGEX = /^[a-zA-Z0-9 ]*$/;

  const handleBulkFileChange = (e) => {
    const file = e.target.files?.[0];

    // Clear previous file error
    setFileError("");

    if (!file) {
      setBulkFile(null);
      return;
    }

    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf("."));

    if (fileExtension !== ".csv") {
      setBulkFile(null);
      setFileError("Invalid file type. Please select a CSV (.csv) file.");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      return;
    }

    setBulkFile(file);
  };

  const handleBulkSubmit = () => {
    if (!hasAcceptedBulkResetTerms || bulkConsentInFlightRef.current) return;

    if (!bulkFile || !bulkComment.trim()) {
      return;
    }

    if (bulkComment === "Others" && !bulkOtherReason.trim()) {
      return;
    }

    if (
      bulkComment === "Others" &&
      !OTHER_REASON_REGEX.test(bulkOtherReason)
    ) {
      return;
    }

    const fileName = bulkFile.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf("."));

    if (fileExtension !== ".csv") {
      setFileError("Invalid file type. Please select a CSV (.csv) file.");
      setBulkFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      return;
    }

    const finalComment =
      bulkComment === "Others"
        ? bulkOtherReason.trim()
        : bulkComment.trim();

    const formData = new FormData();

    formData.append("file", bulkFile);
    formData.append("comment", finalComment);
    formData.append("consent_accepted", String(hasAcceptedBulkResetTerms));
    formData.append("consent_terms_version", TERMINAL_RESET_TERMS_VERSION);

    bulkConsentInFlightRef.current = true;
    const request = bulkUnlockTerminals(formData);
    Promise.resolve(request)
      .then(showConsentAuditNotice)
      .catch(() => {
        // The existing Redux action handles its normal error notification.
      })
      .finally(() => {
        bulkConsentInFlightRef.current = false;
      });

    setHasAcceptedBulkResetTerms(false);

    setBulkFile(null);
    setBulkComment("");
    setBulkOtherReason("");
    setFileError("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat("en-NG", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: "Africa/Lagos",
      timeZoneName: "short",
    }).format(date);
  };

  const getBulkApiErrorMessage = () => {
    const apiErrors =
      error?.data?.data?.apiErrors ||
      error?.data?.apiErrors;

    // // console.log(error)

    // console.log("bulkUnlockResponse:", error);
    // console.log("apiErrors:", apiErrors);
    // console.log("apiErrorList:", apiErrors?.apiErrorList);
    // console.log("first error:", apiErrors?.apiErrorList?.[0]);
    // console.log("first error message:", apiErrors?.apiErrorList?.[0]?.message);

    if (!apiErrors?.apiErrorList?.length) {
      return null;
    }

    return apiErrors.apiErrorList
      .map((error) => error?.message)
      .filter(Boolean)
      .join("; ");
  };

  const exportBulkUnlockCSV = () => {
    const result = bulkUnlockResponse?.data?.responseBody;

    if (!result) return;

    const resetTerminalIds = result.resetTerminalIds || [];
    const errors = result.errors || [];

    const requestStatus = bulkUnlockResponse?.data?.requestSuccessful
      ? "Completed"
      : "Failed";

    const reportDate = new Date().toLocaleString("en-NG", {
      timeZone: "Africa/Lagos",
    });

    const rows = [];

    // Report summary
    rows.push(["BULK UNLOCK REPORT"]);
    rows.push(["Report Date", reportDate]);
    rows.push(["Request Status", requestStatus]);
    rows.push(["Successfully Reset", resetTerminalIds.length]);
    rows.push([
      "Error Count",
      result.errorCount != null ? result.errorCount : errors.length,
    ]);
    rows.push(["Failed", errors.length]);
    rows.push([]);

    // Successfully reset terminals
    rows.push(["SUCCESSFULLY RESET TERMINALS"]);
    rows.push(["Terminal ID"]);

    resetTerminalIds.forEach((terminalId) => {
      rows.push([terminalId || "—"]);
    });

    rows.push([]);

    // Failed terminals
    rows.push(["FAILED TERMINALS"]);
    rows.push(["Terminal ID", "Error"]);

    errors.forEach((item) => {
      rows.push([
        item.terminalId || "—",
        item.error || "—",
      ]);
    });

    const csvContent = rows
      .map((row) =>
        row
          .map((value) => {
            const stringValue = String(value ?? "");
            return `"${stringValue.replace(/"/g, '""')}"`;
          })
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `bulk-unlock-report-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const exportBulkUnlockPDF = () => {
    const result = bulkUnlockResponse?.data?.responseBody;

    if (!result) return;

    const resetTerminalIds = result.resetTerminalIds || [];
    const errors = result.errors || [];

    const requestStatus = bulkUnlockResponse.data.requestSuccessful
      ? "Completed"
      : "Failed";

    const reportDate = new Date().toLocaleString("en-NG", {
      timeZone: "Africa/Lagos",
    });

    const errorCount =
      result.errorCount != null ? result.errorCount : errors.length;

    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text("Bulk Unlock Report", 14, 20);

    doc.setFontSize(10);
    doc.text(`Report Date: ${reportDate}`, 14, 28);

    autoTable(doc, {
      startY: 36,
      head: [["Summary", "Value"]],
      body: [
        ["Request Status", requestStatus],
        ["Successfully Reset", String(resetTerminalIds.length)],
        ["Error Count", String(errorCount)],
        ["Failed", String(errors.length)],
      ],
      theme: "grid",
      styles: {
        fontSize: 9,
        cellPadding: 4,
      },
      headStyles: {
        fontStyle: "bold",
      },
    });

    let currentY = doc.lastAutoTable.finalY + 12;

    // Successfully reset terminals
    if (resetTerminalIds.length > 0) {
      doc.setFontSize(13);
      doc.text("Successfully Reset Terminals", 14, currentY);

      currentY += 4;

      autoTable(doc, {
        startY: currentY,
        head: [["Terminal ID"]],
        body: resetTerminalIds.map((terminalId) => [
          terminalId || "—",
        ]),
        theme: "grid",
        styles: {
          fontSize: 9,
          cellPadding: 4,
        },
        headStyles: {
          fontStyle: "bold",
        },
      });

      currentY = doc.lastAutoTable.finalY + 12;
    }

    // Failed terminals
    if (errors.length > 0) {
      doc.setFontSize(13);
      doc.text("Failed Terminals", 14, currentY);

      currentY += 4;

      autoTable(doc, {
        startY: currentY,
        head: [["Terminal ID", "Error"]],
        body: errors.map((item) => [
          item.terminalId || "—",
          item.error || "—",
        ]),
        theme: "grid",
        styles: {
          fontSize: 9,
          cellPadding: 4,
          overflow: "linebreak",
        },
        headStyles: {
          fontStyle: "bold",
        },
        columnStyles: {
          0: {
            cellWidth: 45,
          },
          1: {
            cellWidth: "auto",
          },
        },
      });
    }

    doc.save(
      `bulk-unlock-report-${new Date().toISOString().slice(0, 10)}.pdf`
    );
  };

  const responseBody = showResults && terminalStatusResponse?.data?.responseBody;
  const resetConfirmHeight = Math.min(
    comment === "Others" ? 720 : 580,
    typeof window === "undefined" ? 720 : Math.max(180, window.innerHeight - 48)
  );

  return (
    <div className="container">
      <UserNav />
      <Sidebar />

      <main className="container-body">
        <div className="settings">

            <div className="profile-box" ref={terminalManagementRef}>
              <h1 style={{marginBottom: "2.5rem"}}>Terminal Management</h1>

              {/* =========================
                  TABS
              ========================= */}
              <div
                style={{
                  display: "flex",
                  borderBottom: "1px solid #ddd",
                  marginBottom: "1.5rem",
                  gap: "0.5rem",
                }}
              >
                <button
                  type="button"
                  onClick={() => {setActiveTab("single")
                  clearBulkUnlockResponse();
                  clearErrorResponse();

                  setBulkFile(null);
                  setBulkComment("");
                  setBulkOtherReason("");
                  setFileError("");

                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                  }}
                  style={{
                    padding: "12px 24px",
                    border: "none",
                    borderBottom:
                      activeTab === "single"
                        ? "3px solid #2CB78B"
                        : "3px solid transparent",
                    background:
                      activeTab === "single"
                        ? "#f0faf5"
                        : "transparent",
                    color:
                      activeTab === "single"
                        ? "#047558"
                        : "#555",
                    fontWeight:
                      activeTab === "single"
                        ? "600"
                        : "400",
                    cursor: "pointer",
                  }}
                >
                  Single Terminal
                </button>

                <button
                  type="button"
                  onClick={() => {setActiveTab("bulk");
                  setShowResults(false);
                  setTerminalId("");
                  setComment("");
                  setOtherReason("");
                  error = null;

                  clearBulkUnlockResponse();
                  clearErrorResponse();

                  setBulkFile(null);
                  setBulkComment("");
                  setBulkOtherReason("");
                  setFileError("");

                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }

                  }}
                  style={{
                    padding: "12px 24px",
                    border: "none",
                    borderBottom:
                      activeTab === "bulk"
                        ? "3px solid #2CB78B"
                        : "3px solid transparent",
                    background:
                      activeTab === "bulk"
                        ? "#f0faf5"
                        : "transparent",
                    color:
                      activeTab === "bulk"
                        ? "#047558"
                        : "#555",
                    fontWeight:
                      activeTab === "bulk"
                        ? "600"
                        : "400",
                    cursor: "pointer",
                  }}
                >
                  Bulk Reset
                </button>
              </div>

              {/* =====================================================
                  SINGLE TERMINAL TAB
              ===================================================== */}
              {activeTab === "single" && (
                <>
                  {!responseBody && (  
                    <div className="modal-form">

                      <div className="form-grid">
                        <div className="modal-input-container">
                          <label className="modal-input-label">
                            Terminal ID
                          </label>

                          <input
                            className="modal-input-input"
                            value={terminalId}
                            onChange={(e) => {
                              setTerminalId(e.target.value);
                              setShowResults(false);
                            }}
                            placeholder="e.g. 2011****"
                            disabled={
                              isCheckingStatus ||
                              isResetting
                            }
                          />
                        </div>
                      </div>

                      {isStaff && (
                        <small className="modal-errors">
                          Staff accounts cannot perform terminal
                          management actions.
                        </small>
                      )}

                      <br />

                      
                        <div className="form-grid-4">

                          <div className="modal-input-button">
                            <button
                              type="button"
                              className="modal-action-button"
                              onClick={() => openConfirm("status")}
                              disabled={
                                !terminalId.trim() ||
                                isStaff ||
                                isCheckingStatus ||
                                isResetting
                              }
                            >
                              {isCheckingStatus
                                ? "Checking..."
                                : "Check Status"}
                            </button>
                          </div>

                          <div className="modal-input-button">
                            <button
                              type="button"
                              className="modal-action-button"
                              onClick={() => openConfirm("reset")}
                              disabled={
                                !terminalId.trim() ||
                                isStaff ||
                                isResetting ||
                                isCheckingStatus
                              }
                            >
                              {isResetting
                                ? "Unlocking..."
                                : "Unlock Device"}
                            </button>
                          </div>

                        </div>
                      
                    </div>
                  )}

                  {/* =========================
                      VIOLATION STATUS
                  ========================= */}
                  {responseBody && (
                    <div
                      className="profile-box"
                      style={{ marginTop: "1.5rem" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <h1>Violation Status</h1>

                        <button
                          type="button"
                          className="modal-close-button"
                          onClick={() => {
                            setShowResults(false);
                            setTerminalId("");
                            setComment("");
                            setOtherReason("");
                          }}
                          style={{
                            width: "auto",
                            padding: "8px 24px",
                            height: "auto",
                            marginLeft: "auto",
                          }}
                        >
                          Clear
                        </button>
                      </div>

                      <div className="modal-form">

                        <div className="form-grid">
                          <ResultRow
                            label="Terminal ID"
                            value={responseBody.terminalId}
                          />
                        </div>

                        <div className="form-grid">
                          <ResultRow
                            label="Violation Type"
                            value={responseBody.violationType}
                          />
                        </div>

                        <div className="form-grid">
                          <ResultRow
                            label="Last Center Latitude"
                            value={responseBody.lastCenterLatitude}
                          />
                        </div>

                        <div className="form-grid">
                          <ResultRow
                            label="Last Center Longitude"
                            value={responseBody.lastCenterLongitude}
                          />
                        </div>

                        <div className="form-grid">
                          <ResultRow
                            label="Last Ping Time"
                            value={formatDateTime(
                              responseBody.lastGeofencingViolationCheckDate
                            )}
                          />
                        </div>

                      </div>
                    </div>
                  )}
                </>
              )}

              {/* =====================================================
                  BULK RESET TAB
              ===================================================== */}
              {activeTab === "bulk" && (
                <>
                  {((!bulkUnlockResponse && !error?.data?.apiErrors?.apiErrorList?.[0]?.message)) && (   
                    <div className="modal-form">

                      {((!bulkUnlockResponse && !error?.data?.apiErrors?.apiErrorList?.[0]?.message)) && (   
                        <div className="form-grid">
                          <div className="modal-input-container">

                            <label className="modal-input-label">
                              CSV File
                            </label>

                            <input
                              ref={fileInputRef}
                              type="file"
                              accept=".csv,text/csv"
                              className="modal-input-input"
                              onChange={handleBulkFileChange}
                              disabled={
                                isStaff ||
                                isBulkUnlocking
                              }
                            />

                            {fileError && (
                              <small
                                className="modal-errors"
                                style={{
                                  display: "block",
                                  marginTop: "0.5rem",
                                  color: "#dc3545",
                                }}
                              >
                                {fileError}
                              </small>
                            )}

                          </div>
                        </div>
                      )}

                      {/* BULK RESET REASON */}
                      {((!bulkUnlockResponse && !error?.data?.apiErrors?.apiErrorList?.[0]?.message)) && ( 
                        <div className="form-grid">

                          <div
                            className="modal-input-container"
                            style={{
                              marginBottom: "1rem",
                            }}
                          >
                            <label className="modal-input-label">
                              Reset Reason
                            </label>

                            <select
                              className="modal-input-input"
                              value={bulkComment}
                              onChange={(e) => {
                                setBulkComment(e.target.value);
                                setBulkOtherReason("");
                              }}
                              disabled={
                                isStaff ||
                                isBulkUnlocking
                              }
                            >
                              <option value="">
                                {isGettingViolationReasons
                                  ? "Loading reasons..."
                                  : "Select a reset reason"}
                              </option>

                              {(
                                violationReasons?.data?.responseBody ||
                                []
                              ).map((reason) => (
                                <option
                                  key={reason.id}
                                  value={reason.resetReason}
                                >
                                  {reason.resetReason}
                                </option>
                              ))}

                              <option value="Others">
                                Others
                              </option>
                            </select>
                          </div>

                        </div>
                      )}

                      {/* BULK OTHER REASON */}
                      {bulkComment === "Others" && (
                        <div className="form-grid">

                          <div
                            className="modal-input-container"
                            style={{
                              marginBottom: "1rem",
                            }}
                          >
                            <label className="modal-input-label">
                              Other Reason
                            </label>

                            <textarea
                              className="modal-input-input"
                              rows={3}
                              value={bulkOtherReason}
                              maxLength={OTHER_REASON_MAX_LENGTH}
                              onChange={(e) => {
                                const value = e.target.value;

                                if (
                                  OTHER_REASON_REGEX.test(value)
                                ) {
                                  setBulkOtherReason(value);
                                }
                              }}
                              placeholder="Enter reason (maximum 200 characters)..."
                              disabled={
                                isStaff ||
                                isBulkUnlocking
                              }
                              style={{
                                resize: "vertical",
                              }}
                            />

                            <small
                              style={{
                                color: "#666",
                              }}
                            >
                              {bulkOtherReason.length}/
                              {OTHER_REASON_MAX_LENGTH} characters
                            </small>
                          </div>

                        </div>
                      )}

                      {isStaff && (
                        <small className="modal-errors">
                          Staff accounts cannot perform terminal
                          management actions.
                        </small>
                      )}

                      <br />

                      {((!bulkUnlockResponse && !error?.data?.apiErrors?.apiErrorList?.[0]?.message)) && (
                        <div
                          id="bulk-reset-consent"
                          style={{ marginBottom: "1.25rem", padding: "3rem", border: "1px solid #cfe9dd", borderRadius: "4px", background: "#f0faf5" }}
                        >
                          <a
                            href={TERMINAL_RESET_TERMS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: "inline-block", marginBottom: "2rem", color: "#047558", textDecoration: "underline" }}
                          >
                            Read the Terms of Use 
                            {/* (PDF, opens in a new tab) */}
                          </a>
                          <label
                            htmlFor="bulk-reset-terms"
                            style={{ display: "flex", alignItems: "flex-start", gap: "0.65rem", lineHeight: 1.5, cursor: "pointer" }}
                          >
                            <input
                              id="bulk-reset-terms"
                              type="checkbox"
                              checked={hasAcceptedBulkResetTerms}
                              onChange={(e) => setHasAcceptedBulkResetTerms(e.target.checked)}
                              disabled={isStaff || isBulkUnlocking}
                              required
                              aria-describedby="bulk-reset-terms-note"
                              style={{ width: "18px", height: "18px", marginTop: "3px", flexShrink: 0 }}
                            />
                            <span>
                              I accept NIBSS Terms and Conditions.
                              {/* I agree to the Terms of Use and certify that the update for every terminal in this file is a Permitted Update and not a Restricted Change. */}
                            </span>
                          </label>
                          <small id="bulk-reset-terms-note" style={{ display: "block", marginTop: "1rem", color: "#555" }}>
                            You must acknowledge this for each reset request before you can submit.
                          </small>
                        </div>
                      )}

                      {((!bulkUnlockResponse && !error?.data?.apiErrors?.apiErrorList?.[0]?.message)) && (  
                        <div className="form-grid-4">

                          <div className="modal-input-button">

                            <button
                              type="button"
                              className="modal-action-button"
                              onClick={handleBulkSubmit}
                              disabled={
                                !hasAcceptedBulkResetTerms ||
                                !bulkFile ||
                                !bulkComment.trim() ||
                                (
                                  bulkComment === "Others" &&
                                  !bulkOtherReason.trim()
                                ) ||
                                !!fileError ||
                                isStaff ||
                                isBulkUnlocking
                              }
                            >
                              {isBulkUnlocking
                                ? "Processing..."
                                : "Submit Bulk Reset"}
                            </button>

                          </div>

                        </div>
                      )}

                    </div>
                  )}

                  {/* =================================================
                      BULK RESULT
                  ================================================= */}
                  {(bulkUnlockResponse?.data?.responseBody || error?.data?.apiErrors?.apiErrorList?.[0]?.message) && ( // && (!error?.code && error?.message)
                    <div
                      className="profile-box"
                      style={{ marginTop: "1.5rem" }}
                    >
                      <h1
                        style={{
                          marginBottom: "2rem",
                        }}
                      >
                        Bulk Reset Result
                      </h1>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >

                        {(bulkUnlockResponse?.data?.responseBody || !error?.data?.apiErrors?.apiErrorList?.[0]?.message) && (
                          <>
                            <button
                              type="button"
                              className="modal-action-button"
                              onClick={exportBulkUnlockCSV}
                              style={{
                                width: "auto",
                                padding: "8px 24px",
                                height: "auto",
                              }}
                            >
                              Export CSV
                            </button>

                            <button
                              type="button"
                              className="modal-action-button"
                              onClick={exportBulkUnlockPDF}
                              style={{
                                width: "auto",
                                padding: "8px 24px",
                                height: "auto",
                              }}
                            >
                              Export PDF
                            </button>
                          </>
                        )}

                        <button
                          type="button"
                          className="modal-close-button"
                          onClick={() => {
                            clearBulkUnlockResponse();
                            clearErrorResponse();

                            setBulkFile(null);
                            setBulkComment("");
                            setBulkOtherReason("");
                            setFileError("");

                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                            }
                          }}
                          style={{
                            width: "auto",
                            padding: "8px 24px",
                            height: "auto",
                          }}
                        >
                          Clear
                        </button>

                      </div>

                      {/* KEEP YOUR EXISTING BULK RESULT BODY HERE */}

                      {(() => {
                        const result =
                          bulkUnlockResponse?.data?.responseBody;
                        // console.log(result)

                        const apiErrorMessage = 
                          // error?.data?.apiErrors?.apiErrorList?.[0]?.message
                          getBulkApiErrorMessage();
                        // console.log(apiErrorMessage)

                        if (!result && apiErrorMessage) {
                          return (
                            <div className="modal-form">
                              <div className="form-grid">

                                <ResultRow
                                  label="Request Status"
                                  value="Failed"
                                />

                              </div>

                              <div
                                style={{
                                  marginTop: "1.5rem",
                                  padding: "1rem",
                                  border: "1px solid #f5c2c7",
                                  background: "#f8d7da",
                                  borderRadius: "4px",
                                }}
                              >
                                <strong
                                  style={{
                                    display: "block",
                                    marginBottom: "0.5rem",
                                  }}
                                >
                                  Bulk Reset Failed
                                </strong>

                                <div>
                                  {apiErrorMessage}
                                </div>
                              </div>
                            </div>
                          );
                        }

                        if (!result) {
                          return null;
                        }

                        const resetTerminalIds =
                          result.resetTerminalIds || [];

                        const errors =
                          result.errors || [];

                        return (
                          <div className="modal-form">

                            <div className="form-grid">

                              <ResultRow
                                label="Request Status"
                                value={
                                  bulkUnlockResponse.data
                                    .requestSuccessful
                                    ? "Completed"
                                    : "Failed"
                                }
                              />

                              <ResultRow
                                label="Successfully Reset"
                                value={
                                  resetTerminalIds.length
                                }
                              />

                            </div>

                            <div
                              className="form-grid"
                              style={{
                                marginBottom: "3.5rem",
                              }}
                            >

                              <ResultRow
                                label="Error Count"
                                value={
                                  result.errorCount != null
                                    ? result.errorCount
                                    : errors.length
                                }
                              />

                              <ResultRow
                                label="Failed"
                                value={errors.length}
                              />

                            </div>

                            {errors.length === 0 && (
                              <div
                                style={{
                                  marginTop: "1.5rem",
                                  padding: "1rem",
                                  border:
                                    "1px solid #2CB78B",
                                  background: "#f0faf5",
                                }}
                              >
                                <strong>
                                  All submitted terminals were
                                  reset successfully.
                                </strong>
                              </div>
                            )}

                          </div>
                        );
                      })()}
                    </div>
                  )}
                </>
              )}
            </div>
        </div>
      </main>

      {/* Confirm Modal */}
      <UserModal
        open={showConfirmModal}
        close={() => {
          setShowConfirmModal(false);
          setHasAcceptedResetTerms(false);
        }}
        height={pendingAction === "reset" ? String(resetConfirmHeight) : "280"}
        width={"550"}
        customStyles={confirmModalStyles}
        style={{ margin: "1rem" }}
        // style={{ margin: "1rem", display: "flex", flexDirection: "column", justifyContent: "center" }} // justifyContent: "space-between"
      >
        <div className="modal-header3">
          <h1>{pendingAction === "status" ? "CHECK TERMINAL STATUS" : "UNLOCK DEVICE"}</h1>
        </div>
        <div className="modal-form" style={pendingAction === "reset" ? { maxHeight: `${resetConfirmHeight - 100}px`, overflowY: "auto" } : undefined}>
          <p style={{ marginBottom: "1rem" }}>
            {pendingAction === "status"
              ? "Check violation status for terminal ID: "
              : "Unlock device for terminal ID: "}
            <strong>{terminalId}</strong>?
          </p>
          {pendingAction === "reset" && (
            <div
              className="modal-input-container"
              style={{ marginBottom: "1rem" }}
            >
              <label className="modal-input-label">
                Reset Reason
              </label>

              <select
                className="modal-input-input"
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                  setOtherReason("");
                }}
                disabled={isResetting}
              >
                <option value="">
                  {isGettingViolationReasons
                    ? "Loading reasons..."
                    : "Select a reset reason"}
                </option>

                {(violationReasons?.data?.responseBody || []).map((reason) => (
                  <option
                    key={reason.id}
                    value={reason.resetReason}
                  >
                    {reason.resetReason}
                  </option>
                ))}

                <option value="Others">Others</option>
              </select>
            </div>
          )}
          
          {comment === "Others" && (
            <div
              className="modal-input-container"
              style={{ marginBottom: "1rem" }}
            >
              <label className="modal-input-label">
                Other Reason
              </label>

              <textarea
                className="modal-input-input"
                rows={3}
                value={otherReason}
                maxLength={200}
                onChange={(e) => {
                  const value = e.target.value;

                  if (OTHER_REASON_REGEX.test(value)) {
                    setOtherReason(value);
                  }
                }}
                placeholder="Enter reason (maximum 200 characters)..."
                disabled={isResetting}
                style={{ resize: "vertical", marginBottom: "1rem" }}
              />

              <small style={{ color: "#666" }}>
                {otherReason.length}/200 characters
              </small>
            </div>
          )}

          {pendingAction === "reset" && (
            <div
              id="single-reset-consent"
              style={{ marginBottom: "1.25rem", padding: "3rem", border: "1px solid #cfe9dd", borderRadius: "4px", background: "#f0faf5" }}
            >
              <a
                href={TERMINAL_RESET_TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-block", marginBottom: "2rem", color: "#047558", textDecoration: "underline" }}
              >
                Read the Terms of Use 
                {/* (PDF, opens in a new tab) */}
              </a>
              <label
                htmlFor="terminal-reset-terms"
                style={{ display: "flex", alignItems: "flex-start", gap: "0.65rem", lineHeight: 1.5, cursor: "pointer" }}
              >
                <input
                  id="terminal-reset-terms"
                  type="checkbox"
                  checked={hasAcceptedResetTerms}
                  onChange={(e) => setHasAcceptedResetTerms(e.target.checked)}
                  disabled={isResetting || isCheckingStatus}
                  required
                  aria-describedby="terminal-reset-terms-note"
                  style={{ width: "18px", height: "18px", marginTop: "3px", flexShrink: 0 }}
                />
                <span>
                  I accept NIBSS Terms and Conditions.
                  {/* I agree to the Terms of Use and certify that the update for this terminal is a Permitted Update and not a Restricted Change. */}
                </span>
              </label>
              <small id="terminal-reset-terms-note" style={{ display: "block", marginTop: "1rem", color: "#555" }}>
                You must acknowledge this for each reset request before you can confirm.
              </small>
            </div>
          )}

          <div className="form-grid-4">
            <div className="modal-input-button">
              <button
                type="button"
                className="modal-close-button"
                onClick={() => {
                  setShowConfirmModal(false);
                  setHasAcceptedResetTerms(false);
                  setComment("");
                  setOtherReason("");
                  setBulkComment("");
                  setBulkOtherReason("");
                }}
              >
                Cancel
              </button>
            </div>
            <div className="modal-input-button">
              <button
                type="button"
                className="modal-action-button"
                onClick={handleConfirm}
                disabled={pendingAction === "reset" &&   ( !comment.trim() || (comment === "Others" && !otherReason.trim()) || !hasAcceptedResetTerms) || isResetting || isCheckingStatus}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      </UserModal>
    </div>
  );
};

// const mapStateToProps = (state) => ({
//   isCheckingStatus: state.pos.isCheckingTerminalStatus,
//   isResetting: state.pos.isResettingTerminal,
//   isBulkUnlocking: state.pos.isBulkUnlocking,
//   terminalStatusResponse: state.pos.terminalStatusResponse,
//   bulkUnlockResponse: state.pos.bulkUnlockResponse,
//   certificates: state.pos.certificates,
// });

const mapStateToProps = (state) => {
  console.log("===== POS REDUX STATE =====");
  console.log(state.pos);

  return {
    isCheckingStatus: state.pos.isCheckingTerminalStatus,
    isResetting: state.pos.isResettingTerminal,
    isBulkUnlocking: state.pos.isBulkUnlocking,
    terminalStatusResponse: state.pos.terminalStatusResponse,
    bulkUnlockResponse: state.pos.bulkUnlockResponse,
    certificates: state.pos.certificates,
    error: state.pos.error,
    isGettingViolationReasons: state.pos.isGettingViolationReasons,
    violationReasons: state.pos.violationReasons,
  };
};

const mapDispatchToProps = (dispatch) => ({
  checkTerminalStatus: (payload) => dispatch(checkTerminalStatus(payload)),
  resetTerminal: (payload) => dispatch(resetTerminal(payload)),
  bulkUnlockTerminals: (formData) => dispatch(bulkUnlockTerminals(formData)),
  getCertificates: () => dispatch(getCertificates()),
  clearBulkUnlockResponse: () => dispatch(clearBulkUnlockResponse()),
  clearErrorResponse: () => dispatch(clearErrorResponse()),
  getViolationReasons: () => dispatch(getViolationReasons()),
});

TerminalManagement.propTypes = {
  checkTerminalStatus: PropTypes.func.isRequired,
  resetTerminal: PropTypes.func.isRequired,
  bulkUnlockTerminals: PropTypes.func.isRequired,
  getCertificates: PropTypes.func.isRequired,
  clearBulkUnlockResponse: PropTypes.func.isRequired,
  isCheckingStatus: PropTypes.bool,
  isResetting: PropTypes.bool,
  isBulkUnlocking: PropTypes.bool,
  terminalStatusResponse: PropTypes.any,
  bulkUnlockResponse: PropTypes.any,
  certificates: PropTypes.array,
  error: PropTypes.any,
  clearErrorResponse: PropTypes.func.isRequired,
  getViolationReasons: PropTypes.func.isRequired,
  isGettingViolationReasons: PropTypes.bool,
  violationReasons: PropTypes.array,
};

ResultRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
};

export default connect(mapStateToProps, mapDispatchToProps)(TerminalManagement);
